import type { ErrorKind, ErrorReportInput } from '@brain/contracts'
import { MAX_ERROR_BATCH } from '@brain/contracts'
import { authFetch } from './api.ts'
import { breadcrumb, getBreadcrumbs } from './breadcrumbs.ts'
import { isTauri } from './platform.ts'

/**
 * Fehlerprotokoll, client side (ADR 0022): uncaught exceptions, unhandled rejections, render errors, UI freezes and
 * navigation loops are queued with a short history ("breadcrumbs": last navigations, clicks, failed requests) and
 * sent to /api/v1/errors. The queue lives in localStorage – written synchronously, so a report survives a frozen
 * tab that has to be closed; it is sent on the next start.
 */

const QUEUE_KEY = 'brain.errorQueue'
const FREEZE_DB = 'brain-watchdog'
const MAX_QUEUE = 50
/** the same message is reported at most once per this window (a render loop must not flood the server) */
const DEDUPE_MS = 60_000

const build = typeof __BUILD_TIME__ === 'string' ? __BUILD_TIME__ : 'dev'
const recent = new Map<string, number>()
let flushTimer: ReturnType<typeof setTimeout> | null = null
let installed = false

function readQueue(): ErrorReportInput[] {
  try {
    const raw = globalThis.localStorage?.getItem(QUEUE_KEY)
    const q = raw ? (JSON.parse(raw) as unknown) : []
    return Array.isArray(q) ? (q as ErrorReportInput[]) : []
  } catch {
    return []
  }
}

function writeQueue(q: ErrorReportInput[]): void {
  try {
    if (q.length) globalThis.localStorage?.setItem(QUEUE_KEY, JSON.stringify(q.slice(-MAX_QUEUE)))
    else globalThis.localStorage?.removeItem(QUEUE_KEY)
  } catch {
    // storage full or blocked – the report is lost, nothing else to do
  }
}

function errorParts(err: unknown): { message: string; stack?: string } {
  if (err instanceof Error)
    return { message: err.message || err.name, ...(err.stack ? { stack: err.stack } : {}) }
  if (typeof err === 'string') return { message: err }
  try {
    return { message: JSON.stringify(err) ?? String(err) }
  } catch {
    return { message: String(err) }
  }
}

/** Queues an error for the Fehlerprotokoll. Never throws. */
export function reportError(kind: ErrorKind, err: unknown, context: Record<string, unknown> = {}): void {
  try {
    const { message, stack } = errorParts(err)
    if (!message || isNoise(message)) return
    const key = `${kind}|${message}`
    const now = Date.now()
    if (now - (recent.get(key) ?? 0) < DEDUPE_MS) return
    recent.set(key, now)
    const report: ErrorReportInput = {
      source: isTauri ? 'desktop' : 'client',
      kind,
      message: message.slice(0, 2000),
      ...(stack ? { stack: stack.slice(0, 8000) } : {}),
      context: {
        route: `${location.pathname}${location.search}`,
        build,
        breadcrumbs: getBreadcrumbs(),
        ...context,
      },
      at: new Date().toISOString(),
    }
    writeQueue([...readQueue(), report])
    scheduleFlush()
  } catch {
    // reporting must never break the app
  }
}

/** Browser/extension noise that says nothing about the app. */
function isNoise(message: string): boolean {
  return (
    /ResizeObserver loop/.test(message) ||
    /^Script error\.?$/.test(message) ||
    /AbortError|The user aborted a request|signal is aborted/.test(message)
  )
}

function scheduleFlush(delay = 2000): void {
  if (flushTimer) return
  flushTimer = setTimeout(() => {
    flushTimer = null
    void flush()
  }, delay)
}

let flushing = false
/** Sends the queue; on failure (offline, signed out) it stays for the next attempt. */
export async function flush(): Promise<void> {
  if (flushing) return
  const q = readQueue()
  if (!q.length) return
  flushing = true
  const batch = q.slice(0, MAX_ERROR_BATCH)
  try {
    const res = await authFetch('/api/v1/errors', {
      method: 'POST',
      body: JSON.stringify({ reports: batch }),
      keepalive: true,
    })
    // 400: a malformed report would block the queue forever – drop the batch
    if (res.ok || res.status === 400) {
      writeQueue(readQueue().slice(batch.length))
      if (readQueue().length) scheduleFlush(500)
    }
  } catch {
    // offline – retried on the next report or start
  } finally {
    flushing = false
  }
}

// ── freeze watchdog ──────────────────────────────────────────────────────────────────────────

/** A freeze the watchdog worker noted while the main thread did not answer (read on the next start). */
interface PendingFreeze {
  startedAt: number
  route: string
  breadcrumbs: string[]
}

function openFreezeDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(FREEZE_DB, 1)
    req.onupgradeneeded = () => req.result.createObjectStore('freeze')
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

/** A freeze recorded in an earlier session that never recovered (tab/app had to be closed) → report it now. */
async function reportStaleFreeze(): Promise<void> {
  if (typeof indexedDB === 'undefined') return
  const db = await openFreezeDb()
  const pending = await new Promise<PendingFreeze | undefined>((resolve) => {
    const tx = db.transaction('freeze', 'readwrite')
    const store = tx.objectStore('freeze')
    const get = store.get('pending')
    get.onsuccess = () => {
      if (get.result) store.delete('pending')
      resolve(get.result as PendingFreeze | undefined)
    }
    get.onerror = () => resolve(undefined)
  })
  db.close()
  if (!pending) return
  reportError('freeze', 'Oberfläche eingefroren – App/Tab musste geschlossen werden', {
    route: pending.route,
    frozenAt: new Date(pending.startedAt).toISOString(),
    breadcrumbs: pending.breadcrumbs,
  })
}

function startWatchdog(): void {
  if (typeof Worker === 'undefined') return
  let worker: Worker
  try {
    worker = new Worker(new URL('./freeze-watchdog.worker.ts', import.meta.url), { type: 'module' })
  } catch {
    return
  }
  const beat = () =>
    worker.postMessage({
      type: 'beat',
      visible: document.visibilityState === 'visible',
      route: `${location.pathname}${location.search}`,
      breadcrumbs: getBreadcrumbs(),
    })
  beat()
  setInterval(beat, 1000)
  document.addEventListener('visibilitychange', beat)
  worker.onmessage = (
    e: MessageEvent<{ type: string; ms: number; route: string; breadcrumbs: string[] }>,
  ) => {
    if (e.data.type !== 'freeze') return
    reportError('freeze', `Oberfläche ${Math.round(e.data.ms / 1000)} s eingefroren`, {
      route: e.data.route,
      frozenMs: e.data.ms,
      breadcrumbs: e.data.breadcrumbs,
    })
  }
}

// ── navigation loop ──────────────────────────────────────────────────────────────────────────

const navTimes: number[] = []
/** Called on every router navigation: many navigations within a second are a loop (e.g. two effects fighting). */
export function noteNavigation(href: string): void {
  breadcrumb(`→ ${href}`)
  const now = Date.now()
  navTimes.push(now)
  while (navTimes.length && now - (navTimes[0] as number) > 2000) navTimes.shift()
  if (navTimes.length === 20)
    reportError('nav_loop', `Navigationsschleife: 20 Seitenwechsel in 2 s`, { href })
}

// ── install ──────────────────────────────────────────────────────────────────────────────────

function describe(el: Element | null): string | null {
  const target = el?.closest('button, a, [role="button"], [role="menuitem"], [role="tab"], input, select')
  if (!target) return null
  const label =
    target.getAttribute('aria-label') ||
    target.getAttribute('title') ||
    (target as HTMLElement).innerText?.trim().split('\n')[0] ||
    target.getAttribute('href') ||
    ''
  return `klick ${target.tagName.toLowerCase()} "${label.slice(0, 50)}"`
}

/** Global handlers – once, in main.tsx. */
export function installErrorReporting(): void {
  if (installed || typeof window === 'undefined') return
  installed = true
  window.addEventListener('error', (e) => {
    // resource load errors (img/script) have no `error`
    if (e.error || e.message)
      reportError('exception', e.error ?? e.message, { at: `${e.filename}:${e.lineno}` })
  })
  window.addEventListener('unhandledrejection', (e) => reportError('unhandled', e.reason))
  document.addEventListener(
    'click',
    (e) => {
      const d = describe(e.target instanceof Element ? e.target : null)
      if (d) breadcrumb(d)
    },
    { capture: true, passive: true },
  )
  window.addEventListener('online', () => void flush())
  startWatchdog()
  void reportStaleFreeze().catch(() => null)
  scheduleFlush(3000)
}
